/**
 * Points allocation for the jury vote (`juryVotingType: 'points'`).
 *
 * Bigfoot-only: the juror rates every project on a fixed scale and may additionally be capped by a
 * total budget. Shared by the jury card (which derives the allowed range) and the backend (which
 * validates what the card sends), so both sides agree on the same numbers.
 */
/** Fallback values used when the configuration does not provide the fields. */
export const DEFAULT_JURY_POINTS_CONFIGURATION = Object.freeze({
    maxPointsPerProject: 5,
    minPointsPerProject: 0,
    isTotalPointsLimitEnabled: false,
    maxTotalPoints: null,
    mustRateAllProjects: false,
});
/**
 * Determines the active voting type. Unknown or missing values fall back to funding.
 *
 * @param config Client configuration.
 * @returns `'points'` only when the configuration explicitly says so.
 */
export const getJuryVotingType = (config) => config?.juryVotingType === 'points' ? 'points' : 'funding';
/**
 * Returns the points configuration including defaults. Required because `req.config` is loaded
 * with `.lean()`, where Mongoose defaults do not apply. Subdocuments are unwrapped first so
 * spreading copies the fields instead of the internal Mongoose properties.
 *
 * @param config Client configuration.
 * @returns The stored configuration merged over the defaults.
 */
export const getJuryPointsConfiguration = (config) => {
    const storedConfiguration = config?.juryPointsConfiguration;
    const plainConfiguration = typeof storedConfiguration?.toObject === 'function' ? storedConfiguration.toObject() : storedConfiguration;
    return {
        ...DEFAULT_JURY_POINTS_CONFIGURATION,
        ...(plainConfiguration || {}),
    };
};
/**
 * Sum of the points given by a juror.
 *
 * @param juror Juror document (only `points` is read).
 * @returns The sum, `0` when the juror has no ratings.
 */
export const sumJurorPoints = (juror) => (juror?.points || []).reduce((sum, entry) => sum + entry.points, 0);
/**
 * Sum of a `projectId → points` map, i.e. the budget already spent.
 *
 * @param pointsByProject Map of explicit ratings.
 * @returns The sum, `0` for an empty map.
 */
export const sumPoints = (pointsByProject) => Object.values(pointsByProject ?? {}).reduce((sum, value) => sum + value, 0);
/**
 * Points budget that is still available for one project.
 *
 * @param configuration        Points configuration.
 * @param pointsOfOtherProjects Sum of all points except the project being edited.
 * @returns The remaining budget, or `null` when no total limit is configured.
 */
export const remainingPointsBudget = (configuration, pointsOfOtherProjects) => configuration.isTotalPointsLimitEnabled && configuration.maxTotalPoints !== null
    ? configuration.maxTotalPoints - pointsOfOtherProjects
    : null;
/**
 * Highest value that may be given to one project: the scale maximum, additionally capped by the
 * remaining budget but never below the scale minimum.
 *
 * @param configuration        Points configuration.
 * @param pointsOfOtherProjects Sum of all points except the project being edited.
 * @returns The maximum assignable value.
 */
export const maxPointsForProject = (configuration, pointsOfOtherProjects) => {
    const remainingBudget = remainingPointsBudget(configuration, pointsOfOtherProjects);
    return remainingBudget === null
        ? configuration.maxPointsPerProject
        : Math.max(configuration.minPointsPerProject, Math.min(configuration.maxPointsPerProject, remainingBudget));
};
/**
 * Value the "+" button jumps to.
 *
 * @param minPoints     Scale minimum.
 * @param assignedPoints Current rating, `undefined` when the project is not rated yet.
 * @returns The next increment value.
 */
export const nextIncrementValue = (minPoints, assignedPoints) => assignedPoints !== undefined ? assignedPoints + 1 : Math.max(minPoints, 1);
/**
 * Clamps a typed value into the allowed range.
 *
 * @param value Value entered by the juror.
 * @param min   Lowest allowed value.
 * @param max   Highest allowed value.
 * @returns The clamped value.
 */
export const clampPoints = (value, min, max) => Math.min(Math.max(value, min), max);
/**
 * Validates a single points value against the configuration.
 *
 * @param pointsConfiguration Points configuration.
 * @param value               Value submitted by the juror.
 * @returns `null` when valid, otherwise the error message for the client.
 */
export const validatePointsValue = (pointsConfiguration, value) => {
    if (!Number.isInteger(value)) {
        return 'Die Punktzahl muss eine ganze Zahl sein.';
    }
    const { minPointsPerProject, maxPointsPerProject } = pointsConfiguration;
    if (value < minPointsPerProject || value > maxPointsPerProject) {
        return `Die Punktzahl muss zwischen ${minPointsPerProject} und ${maxPointsPerProject} liegen.`;
    }
    return null;
};
/**
 * Validates the total budget.
 *
 * @param pointsConfiguration  Points configuration.
 * @param pointsOfOtherProjects Sum of all points except the project being set.
 * @param value                Value submitted by the juror.
 * @returns `null` when valid, otherwise the error message for the client.
 */
export const validateTotalPointsBudget = (pointsConfiguration, pointsOfOtherProjects, value) => {
    const { isTotalPointsLimitEnabled, maxTotalPoints } = pointsConfiguration;
    if (!isTotalPointsLimitEnabled || !Number.isFinite(maxTotalPoints)) {
        return null;
    }
    const remainingPoints = Math.max(maxTotalPoints - pointsOfOtherProjects, 0);
    if (value <= remainingPoints) {
        return null;
    }
    return `Das Gesamtbudget von ${maxTotalPoints} Punkten wird überschritten. Es sind noch ${remainingPoints} Punkte verfügbar.`;
};
//# sourceMappingURL=points.js.map